import type { Map as MlMap, GeoJSONSource } from "maplibre-gl";

/**
 * v0.9.10a item 7 (owner choice: ป้ายโยงเส้น) — when the dams' own labels would overlap EACH OTHER (far zoom,
 * e.g. the whole-country view on a phone: 11 dams inside ~60×90 px), the labels leave the pins and line up
 * in a column beside the cluster, each joined to its pin by a thin leader line. One line per dam:
 * "<ชื่อ> <% หรือค่าระบาย>" — v0.9.10b: no "ปี 54" in a callout (smaller labels, fewer crossing lines); "ปี 54"
 * comes back under the pins once the zoom un-crowds them. After placing, pairs of callouts whose leader lines
 * cross swap places when that removes crossings (data-crossings = what is left).
 * No overlap → the normal labels under the pins (dam-layer.ts) and no callouts.
 * Tap a callout = tap that dam (its card opens). Positions are measured from the real UI after the
 * map stops moving, so the column never sits under the search box / day bar / buttons / sheet / top-right group.
 */
import { farLabelBoxes } from "./river-labels-far";
const DAM_LAYER = "dams-layer", DAM_SRC = "dams-src", Y54_LAYER = "dams-y54-label", Y54_SRC = "dams-y54";
const ROW_H = 18, GAP = 2, PAD = 6, ICON_H = 22, FONT = 11;

let mapRef: MlMap | null = null;
let host: HTMLDivElement | null = null;
let svg: SVGSVGElement | null = null;
let active = false;
let raf = 0;
const measureCtx = document.createElement("canvas").getContext("2d");

interface Pin { name: string; value: string; y54: string | null; x: number; y: number; lngLat: [number, number] }

function textW(t: string, px: number): number {
  if (!measureCtx) return t.length * px * 0.6;
  measureCtx.font = `600 ${px}px "IBM Plex Sans Thai", "Noto Sans", sans-serif`;
  return measureCtx.measureText(t).width;
}
const vis = (q: string): DOMRect | null => {
  const e = document.querySelector<HTMLElement>(q); if (!e || e.hidden) return null;
  const cs = getComputedStyle(e); if (cs.display === "none" || cs.visibility === "hidden") return null;
  const r = e.getBoundingClientRect(); return r.width > 0 && r.height > 0 ? r : null;
};

/** The dams on screen, with their own label box (as dam-layer.ts draws it under the pin). */
function pins(m: MlMap): Pin[] {
  const src = m.getSource(DAM_SRC) as (GeoJSONSource & { _data?: GeoJSON.FeatureCollection }) | undefined;
  const feats = src?._data?.features ?? [];
  const y54on = m.getLayer(Y54_LAYER) && m.getLayoutProperty(Y54_LAYER, "visibility") !== "none";
  const y54 = new Map<string, string>();
  if (y54on) for (const f of (m.getSource(Y54_SRC) as (GeoJSONSource & { _data?: GeoJSON.FeatureCollection }) | undefined)?._data?.features ?? []) {
    const c = (f.geometry as GeoJSON.Point).coordinates; y54.set(`${c[0]},${c[1]}`, String(f.properties?.t ?? ""));
  }
  const W = window.innerWidth, H = window.innerHeight, out: Pin[] = [];
  for (const f of feats) {
    const c = (f.geometry as GeoJSON.Point).coordinates as [number, number];
    const p = m.project(c);
    if (p.x < 0 || p.x > W || p.y < 0 || p.y > H) continue;
    const name = String(f.properties?.name ?? "") + (f.properties?.stale ? " (เก่า)" : "");
    out.push({ name, value: String(f.properties?.pct ?? ""), y54: y54.get(`${c[0]},${c[1]}`) ?? null, x: p.x, y: p.y, lngLat: c });
  }
  return out;
}
/** Would the pins' own labels overlap each other? (two lines under each pin + "ปี 54" when on) */
function crowded(ps: Pin[]): boolean {
  const boxes = ps.map((p) => {
    const w = Math.max(textW(p.name, 11), textW(p.value, 13), p.y54 ? textW(p.y54, 11) : 0) + 6;
    const h = 30 + (p.y54 ? 14 : 0);
    return { x0: p.x - w / 2, x1: p.x + w / 2, y0: p.y - ICON_H, y1: p.y + 3 + h };
  });
  for (let i = 0; i < boxes.length; i++) for (let j = i + 1; j < boxes.length; j++) {
    const a = boxes[i]!, b = boxes[j]!;
    if (!(a.x1 <= b.x0 || b.x1 <= a.x0 || a.y1 <= b.y0 || b.y1 <= a.y0)) return true;
  }
  return false;
}

function ensureDom(): void {
  if (host) return;
  host = document.createElement("div"); host.className = "dam-callouts"; host.setAttribute("aria-label", "ชื่อเขื่อน");
  svg = document.createElementNS("http://www.w3.org/2000/svg", "svg"); svg.setAttribute("class", "dam-co-lines"); svg.setAttribute("aria-hidden", "true");
  document.body.append(svg, host);
}
let savedField: unknown = null;
function setMapText(m: MlMap, hidden: boolean): void {
  // callouts on → the pins' own text leaves the map entirely (no hidden box keeping river names away)
  if (m.getLayer(DAM_LAYER)) {
    const cur = m.getLayoutProperty(DAM_LAYER, "text-field");
    if (hidden && cur !== "") { savedField = cur; m.setLayoutProperty(DAM_LAYER, "text-field", ""); }
    else if (!hidden && cur === "" && savedField != null) m.setLayoutProperty(DAM_LAYER, "text-field", savedField as never);
  }
  // the pins' own text steps aside while the callouts show it (the icons stay)
  for (const id of [DAM_LAYER, Y54_LAYER]) {
    if (!m.getLayer(id)) continue;
    const isZero = m.getPaintProperty(id, "text-opacity") === 0;
    if (hidden === isZero) continue; // already right
    m.setPaintProperty(id, "text-opacity", hidden ? 0 : id === DAM_LAYER ? ["case", ["get", "stale"], 0.7, 1] : 1);
  }
}

function layout(): void {
  raf = 0;
  const m = mapRef; if (!m || !host || !svg) return;
  // Rebuilding all callout DOM and collision boxes during every gesture blocks input.
  // Hide the screen-space overlay while moving; map pins still track the camera.
  host.hidden = m.isMoving();
  svg.style.display = m.isMoving() ? "none" : "";
  if (m.isMoving()) return;
  const damsOn = m.getLayer(DAM_LAYER) && m.getLayoutProperty(DAM_LAYER, "visibility") !== "none";
  const ps = damsOn && !document.body.classList.contains("past-mode") ? pins(m) : [];
  const want = ps.length > 1 && crowded(ps);
  if (want !== active) { active = want; document.body.classList.toggle("dam-callouts-on", want); }
  // (re)apply every pass — a layer added later (the "ปี 54" toggle) starts with its own opacity
  if (want || m.getPaintProperty(DAM_LAYER, "text-opacity") === 0) setMapText(m, want);
  if (!want) { host.replaceChildren(); svg.replaceChildren(); return; }

  // free map area (same idea as the country-view fit)
  const W = window.innerWidth, H = window.innerHeight;
  let top = 10, bottom = H - 10, rightEdge = W - 6;
  for (const q of [".search-box", ".pr-days", ".rf-chips", "#river-chip"]) { const r = vis(q); if (r && r.top < H / 2) top = Math.max(top, r.bottom + 6); }
  for (const q of ["#locate-fab", "#summary-fab", "#flow-fab", "#sos-fab"]) { const r = vis(q); if (r && r.top > H / 2) bottom = Math.min(bottom, r.top - 6); }
  const sheet = vis(".sheet");
  if (sheet && sheet.left < W / 2 && sheet.top > H / 3) bottom = Math.min(bottom, sheet.top - 6); // phone sheet
  if (sheet && sheet.left > W / 2) rightEdge = Math.min(rightEdge, sheet.left - 6);              // desktop panel
  const stack = vis(".tr-stack");

  const rows = ps.sort((a, b) => a.y - b.y || a.x - b.x).map((p) => ({ p, text: `${p.name} ${p.value}` }));
  // keep-outs: every pin (icon box), the top-right group, and the area already taken by placed callouts
  type R = { x0: number; y0: number; x1: number; y1: number };
  const hit = (a: R, b: R): boolean => !(a.x1 <= b.x0 || b.x1 <= a.x0 || a.y1 <= b.y0 || b.y1 <= a.y0);
  const keep: R[] = ps.map((p) => ({ x0: p.x - 10, y0: p.y - ICON_H - 2, x1: p.x + 10, y1: p.y + 4 }));
  if (stack) keep.push({ x0: stack.left - 4, y0: stack.top - 4, x1: stack.right + 4, y1: stack.bottom + 4 });
  // the far river names keep their places (placed first); the callouts go around them (v0.9.10a — a dam name
  // is never hidden, only moved; the main river names stay readable on small screens)
  const soft: R[] = farLabelBoxes().map((b) => ({ x0: b[0], y0: b[1], x1: b[2], y1: b[3] }));
  const attrib = vis(".tr-stack .maplibregl-ctrl-attrib"); // ⓘ sits beside the column on short phones
  if (attrib) keep.push({ x0: attrib.left - 4, y0: attrib.top - 4, x1: attrib.right + 4, y1: attrib.bottom + 4 });
  for (const q of [".pr-cardhost .pr-card", ".fs-popup"]) { const r = vis(q); if (r) keep.push({ x0: r.left, y0: r.top, x1: r.right, y1: r.bottom }); }
  const placed: Placed[] = [];
  // greedy: each dam (top → bottom) takes the free slot closest to its pin
  for (const r of rows) {
    const w = Math.ceil(textW(r.text, FONT) + 12);
    let best: { box: R; d: number } | null = null;
    // 1) near the pin (±240 px), around the river names · 2) anywhere free, around them · 3) last resort: over a
    // river name (a dam name is never dropped — it outranks river names, SPEC v0.9.10a)
    for (const [reach, avoidSoft] of [[240, true], [2000, true], [2000, false]] as const) {
    const ys = Math.max(top, r.p.y - reach), ye = Math.min(bottom - ROW_H, r.p.y + reach);
    const xs = Math.max(PAD, r.p.x - reach - w), xe = Math.min(rightEdge - w, r.p.x + reach);
    search: for (let y = ys; y <= ye; y += 6) for (let x = xs; x <= xe; x += 8) {
      const box = { x0: x, y0: y, x1: x + w, y1: y + ROW_H };
      const pad = { x0: box.x0 - GAP, y0: box.y0 - GAP, x1: box.x1 + GAP, y1: box.y1 + GAP };
      if (keep.some((k) => hit(pad, k)) || placed.some((q) => hit(pad, q.box)) || (avoidSoft && soft.some((k) => hit(pad, k)))) continue;
      const nx = Math.max(box.x0, Math.min(r.p.x, box.x1)), ny = Math.max(box.y0, Math.min(r.p.y - ICON_H / 2, box.y1));
      const d = Math.hypot(nx - r.p.x, ny - (r.p.y - ICON_H / 2));
      if (!best || d < best.d) { best = { box, d }; if (d < 8) break search; }
    }
    if (best) break;
    }
    if (best) placed.push({ r, box: best.box });
  }
  const free = (box: R, skip: number[]): boolean => box.x0 >= PAD && box.x1 <= rightEdge && box.y0 >= top && box.y1 <= bottom
    && !keep.some((k) => hit(box, k)) && !placed.some((q, i) => !skip.includes(i) && hit(box, q.box)) && !soft.some((k) => hit(box, k));
  const before = crossings(placed);
  untangle(placed, free);
  // still crossing → move one callout at a time to any free spot (coarse grid) that lowers the crossings
  relocate(placed, free, { x0: PAD, x1: rightEdge, y0: top, y1: bottom });
  untangle(placed, free);
  host.dataset.crossingsBefore = String(before);
  host.dataset.crossings = String(crossings(placed));
  host.replaceChildren(); svg.replaceChildren();
  svg.setAttribute("width", String(W)); svg.setAttribute("height", String(H));
  for (const { r, box } of placed) {
    const el = document.createElement("button");
    el.type = "button"; el.className = "dam-co";
    el.style.left = `${box.x0}px`; el.style.top = `${box.y0}px`; el.style.width = `${box.x1 - box.x0}px`;
    el.textContent = r.text;
    el.setAttribute("aria-label", `เขื่อน${r.p.name} ${r.p.value}`);
    el.addEventListener("click", () => {
      // same as tapping the pin itself → its card
      const pt = m.project(r.p.lngLat); pt.y -= ICON_H / 2;
      m.fire("click", { lngLat: m.unproject(pt), point: pt, originalEvent: {} } as never);
    });
    host.append(el);
    const [x1, y1, x2, y2] = leader(r.p, box);
    const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
    line.setAttribute("x1", String(x1)); line.setAttribute("y1", String(y1));
    line.setAttribute("x2", String(x2)); line.setAttribute("y2", String(y2));
    svg.append(line);
  }
  if (placed.length < rows.length) host.dataset.unplaced = rows.filter((r) => !placed.some((q) => q.r === r)).map((r) => r.p.name).join(","); else delete host.dataset.unplaced;
}
type Box = { x0: number; y0: number; x1: number; y1: number };
/** the leader line: pin (icon middle) → nearest point of the callout box */
function leader(p: Pin, box: Box): [number, number, number, number] {
  const px = p.x, py = p.y - ICON_H / 2;
  return [px, py, Math.max(box.x0, Math.min(px, box.x1)), Math.max(box.y0, Math.min(py, box.y1))];
}
function segCross(a: number[], b: number[]): boolean {
  const o = (ax: number, ay: number, bx: number, by: number, cx: number, cy: number): number => Math.sign((bx - ax) * (cy - ay) - (by - ay) * (cx - ax));
  const [p1x, p1y, p2x, p2y] = a as [number, number, number, number], [q1x, q1y, q2x, q2y] = b as [number, number, number, number];
  const d1 = o(q1x, q1y, q2x, q2y, p1x, p1y), d2 = o(q1x, q1y, q2x, q2y, p2x, p2y), d3 = o(p1x, p1y, p2x, p2y, q1x, q1y), d4 = o(p1x, p1y, p2x, p2y, q2x, q2y);
  return d1 * d2 < 0 && d3 * d4 < 0; // proper crossing only (touching / zero-length lines don't count)
}
interface Placed { r: { p: Pin; text: string }; box: Box }
/** number of leader-line pairs that cross */
function crossings(placed: Placed[]): number {
  const ls = placed.map((q) => leader(q.r.p, q.box));
  let n = 0;
  for (let i = 0; i < ls.length; i++) for (let j = i + 1; j < ls.length; j++) if (segCross(ls[i]!, ls[j]!)) n++;
  return n;
}
/** swap the places of two callouts whose lines cross (same top-left, or same right edge for a left-hand column)
 *  whenever the swap is free of pins / other callouts / river names and lowers the total crossings */
function untangle(placed: Placed[], free: (b: Box, skip: number[]) => boolean): void {
  for (let pass = 0; pass < 30; pass++) {
    let improved = false;
    const total = crossings(placed);
    if (!total) return;
    for (let i = 0; i < placed.length && !improved; i++) for (let j = i + 1; j < placed.length && !improved; j++) {
      const A = placed[i]!, B = placed[j]!;
      if (!segCross(leader(A.r.p, A.box), leader(B.r.p, B.box))) continue;
      const wa = A.box.x1 - A.box.x0, wb = B.box.x1 - B.box.x0, h = A.box.y1 - A.box.y0;
      const options: [Box, Box][] = [
        [{ x0: B.box.x0, y0: B.box.y0, x1: B.box.x0 + wa, y1: B.box.y0 + h }, { x0: A.box.x0, y0: A.box.y0, x1: A.box.x0 + wb, y1: A.box.y0 + h }],
        [{ x0: B.box.x1 - wa, y0: B.box.y0, x1: B.box.x1, y1: B.box.y0 + h }, { x0: A.box.x1 - wb, y0: A.box.y0, x1: A.box.x1, y1: A.box.y0 + h }],
      ];
      for (const [na, nb] of options) {
        const pad = (b: Box): Box => ({ x0: b.x0 - GAP, y0: b.y0 - GAP, x1: b.x1 + GAP, y1: b.y1 + GAP });
        const overlap = !(na.x1 + GAP <= nb.x0 || nb.x1 + GAP <= na.x0 || na.y1 + GAP <= nb.y0 || nb.y1 + GAP <= na.y0);
        if (overlap || !free(pad(na), [i, j]) || !free(pad(nb), [i, j])) continue;
        const oa = A.box, ob = B.box;
        A.box = na; B.box = nb;
        if (crossings(placed) < total) { improved = true; break; }
        A.box = oa; B.box = ob;
      }
    }
    if (!improved) return;
  }
}
/** crossings that involve callout i only */
function crossingsOf(placed: Placed[], i: number, box: Box): number {
  const l = leader(placed[i]!.r.p, box);
  let n = 0;
  for (let j = 0; j < placed.length; j++) if (j !== i && segCross(l, leader(placed[j]!.r.p, placed[j]!.box))) n++;
  return n;
}
/** for each callout still crossing another line: try every free spot on a 16 × 12 px grid; take the one with the
 *  fewest crossings (then the shortest line) when it lowers that callout's crossings */
function relocate(placed: Placed[], free: (b: Box, skip: number[]) => boolean, area: Box): void {
  for (let pass = 0; pass < 3; pass++) {
    let moved = false;
    for (let i = 0; i < placed.length; i++) {
      const q = placed[i]!, cur = crossingsOf(placed, i, q.box);
      if (!cur) continue;
      const w = q.box.x1 - q.box.x0, h = q.box.y1 - q.box.y0, px = q.r.p.x, py = q.r.p.y - ICON_H / 2;
      let best: { box: Box; c: number; d: number } | null = null;
      for (let y = area.y0; y + h <= area.y1; y += 12) for (let x = area.x0; x + w <= area.x1; x += 16) {
        const box = { x0: x, y0: y, x1: x + w, y1: y + h };
        if (!free({ x0: x - GAP, y0: y - GAP, x1: x + w + GAP, y1: y + h + GAP }, [i])) continue;
        const c = crossingsOf(placed, i, box);
        if (c >= cur) continue;
        const d = Math.hypot(Math.max(box.x0, Math.min(px, box.x1)) - px, Math.max(box.y0, Math.min(py, box.y1)) - py);
        if (!best || c < best.c || (c === best.c && d < best.d)) best = { box, c, d };
      }
      if (best) { q.box = best.box; moved = true; }
    }
    if (!moved) return;
  }
}
const schedule = (): void => { if (!raf) raf = requestAnimationFrame(layout); };

/** Start once the dam layer exists; re-lays out whenever the map or the page layout moves. */
export function installDamCallouts(m: MlMap): void {
  if (mapRef) return;
  mapRef = m; ensureDom();
  m.on("movestart", schedule); m.on("moveend", schedule); m.on("resize", schedule); m.on("sourcedata", (e) => { if ((e as { sourceId?: string }).sourceId === DAM_SRC || (e as { sourceId?: string }).sourceId === Y54_SRC) schedule(); });
  m.on("styledata", schedule);
  for (const ev of ["resize", "fs-top-layout", "fs-theme", "fs-far-labels"]) window.addEventListener(ev, schedule);
  try { new ResizeObserver(schedule).observe(document.body); } catch { /* old browsers */ }
  window.setInterval(schedule, 1000); // sheet / bars animate on their own
  schedule();
}
/** e2e: the callouts currently shown (empty when the pins' own labels are used). */
export function calloutsShown(): number { return active ? host?.childElementCount ?? 0 : 0; }
